import { Badge, Checkbox, TooltipV2 } from '@cimplico/components';
import { ExclamationTriangleIcon, PaperClipIcon } from '@heroicons/react/24/solid';
import { useDispatch } from 'react-redux';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import { useViewLink } from '../../../../queries/use-view-link';
import { setViewingDocument } from '../../../../slices/documentViewSlice';
import { FileRecordNew, FileRecordTypeEnum, FileStatusEnum } from '../../../../types/file-record';
import { FYI_URL } from '../../../../config';
import { useFileRecord } from '../../../../queries/use-file-record';
import { isFolderType } from '../../../files/file-utils';
import RecordTypeIcon from '../../../files/RecordTypeIcon';
import ViewErrorDialog from '../../../files/ViewErrorDialog';
import { useState } from 'react';
import FyiLogo from '../../../../assets/fyi.png';

type AttachmentRowProps = {
    record: FileRecordNew;
    defaultChecked?: boolean;
    usedOnEntry?: boolean;
    disabled?: boolean;
    onFolderChange?: (id: string) => void;
    onCheckToggle?: (id: string) => void;
};

function AttachmentRow({ usedOnEntry, record, defaultChecked, disabled, onFolderChange, onCheckToggle }: AttachmentRowProps): JSX.Element {
    const [documentViewError, setDocumentViewError] = useState<FileRecordNew | undefined>(undefined);
    const documentViewQuery = useViewLink(record.id, false);
    const documentQuery = useFileRecord(record.source?.id, false);
    const isLegacyFYI =
        (record.provider !== 'FYI' && record.recordType === FileRecordTypeEnum.FYI_DOCUMENT) ||
        (record.source?.provider !== 'FYI' && record.source?.recordType === FileRecordTypeEnum.FYI_DOCUMENT);

    const isFYI = record.recordType === FileRecordTypeEnum.FYI_DOCUMENT || record.source?.recordType === FileRecordTypeEnum.FYI_DOCUMENT;
    const isFolder = isFolderType(record.recordType);

    const dispatch = useDispatch();
    const activeSubscription = useIsActiveSubscription();

    async function handleDocumentView(): Promise<void> {
        if (!activeSubscription) {
            return;
        }

        if (record.id) {
            dispatch(setViewingDocument({ record }));
        }
    }
    return (
        <>
            {documentViewError && <ViewErrorDialog record={documentViewError} onClose={(): void => setDocumentViewError(undefined)} />}
            <div key={record.id} className="flex items-center p-2 pl-4 hover:bg-gray-50 cursor-pointer">
                {/* FOLDER */}
                {isFolder && (
                    <div
                        className="flex items-center gap-2 w-full"
                        onClick={(): void => {
                            onFolderChange && onFolderChange(record.id);
                        }}
                    >
                        <RecordTypeIcon
                            recordType={record.link ? FileRecordTypeEnum.LINK : record.recordType}
                            system={record.systemRecord}
                            locked={record.locked}
                        />
                        <p className="text-xs text-gray-600 cursor-pointer">{record.name}</p>

                        {record.provider === 'FYI' && (
                            <span className="mr-1 ml-auto">
                                <img src={FyiLogo} className="w-6 h-6 shrink-0" />
                            </span>
                        )}
                    </div>
                )}

                {/* OTHER FILES */}
                {!isFolder && (
                    <div className="flex items-center gap-2 w-full">
                        {onCheckToggle && (
                            <Checkbox
                                disabled={disabled}
                                checked={defaultChecked}
                                onChange={(): void => {
                                    onCheckToggle(record.id);
                                }}
                            />
                        )}
                        <RecordTypeIcon
                            recordType={record.link ? FileRecordTypeEnum.LINK : record.recordType}
                            system={record.systemRecord}
                            locked={record.locked}
                        />
                        {/* LINK or LINK REFERENCE */}
                        {(record.recordType === FileRecordTypeEnum.LINK || record.source?.recordType === FileRecordTypeEnum.LINK) && (
                            <a
                                href={record.link || record.source?.link || undefined}
                                target="_blank"
                                className="font-medium text-indigo-600 dark:text-indigo-400 text-xs cursor-pointer max-w-[90%]"
                                rel="noreferrer"
                            >
                                {record.name}
                            </a>
                        )}
                        {/* DOCUMENT or DOCUMENT REFERENCE */}
                        {(record.recordType === FileRecordTypeEnum.DOCUMENT ||
                            record.source?.recordType === FileRecordTypeEnum.DOCUMENT ||
                            record.recordType === FileRecordTypeEnum.FYI_DOCUMENT ||
                            record.source?.recordType === FileRecordTypeEnum.FYI_DOCUMENT) && (
                            <div className="flex items-center justify-between gap-2 w-full">
                                <p
                                    className="font-medium text-indigo-600 dark:text-indigo-400 text-xs cursor-pointer break-all max-w-[90%]"
                                    onClick={handleDocumentView}
                                >
                                    {record.name}
                                </p>

                                {isFYI && (
                                    <div className="ml-auto mr-1 flex items-center gap-1">
                                        <span className="-mt-1 ml-auto">
                                            <img src={FyiLogo} className="w-6 h-6 shrink-0" />
                                        </span>
                                        {isLegacyFYI && (
                                            <span className="ml-auto">
                                                <TooltipV2 message="This is a legacy file." position="left">
                                                    <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                                                </TooltipV2>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div className="ml-auto flex gap-1">
                    {record.status === FileStatusEnum.CHECKED_OUT && (
                        <div>
                            <TooltipV2 message="This file has been checked out">
                                <ExclamationTriangleIcon className="text-red-500 h-4 w-4" />
                            </TooltipV2>
                        </div>
                    )}
                    {usedOnEntry && (
                        <div>
                            <TooltipV2 message="This file has been attached to an entry">
                                <PaperClipIcon className="text-gray-500 h-4 w-4" />
                            </TooltipV2>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
export default AttachmentRow;
